import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  IPortalPropertyPhotoAsset,
  IUpdateSequencePayLoad,
} from '../../../../interfaces/asset';
import * as assetApi from '../../../../api/assets';
import { denormaliseData, normaliseData } from '../helpers';

const useUpdatePhotoSequence = () => {
  const queryClient = useQueryClient();
  const queryKey = ['asset', 'photos'];

  return useMutation({
    mutationFn: assetApi.updatePropertyPhotosSequence,
    onMutate: async (updatedSequence: IUpdateSequencePayLoad) => {
      // prevent any query updates while we perform optimistic update
      await queryClient.cancelQueries({ queryKey });

      // copy the current data as it exists in the react-query store
      const [previousKeyAndData]: any[] = queryClient.getQueriesData({
        queryKey,
      });

      const oldData = previousKeyAndData?.[1] || [];

      // Optimistic update
      queryClient.setQueriesData(
        { queryKey },
        (photoData: IPortalPropertyPhotoAsset[]) => {
          const normalisedData = normaliseData(photoData);
          const property = normalisedData.properties[updatedSequence.NHID];

          if (!property) return photoData;

          for (const tagAssetID of property.tags) {
            const tagObj = normalisedData.tags[tagAssetID];
            tagObj.photos = tagObj.photos
              .map((photoID: number) => {
                const photo = normalisedData.photos[photoID];
                const photoIndex = updatedSequence.AssetIDs.findIndex(
                  (assetID: number) => assetID === photo.AssetID
                );
                if (photoIndex !== -1) {
                  photo.PhotoSequence = photoIndex + 1;
                }
                return photoID;
              })
              .sort((a: number, b: number) => {
                const photoA = normalisedData.photos[a];
                const photoB = normalisedData.photos[b];
                return photoA.PhotoSequence - photoB.PhotoSequence;
              });
          }

          return denormaliseData(normalisedData);
        }
      );

      // this gets added to the context in case we need to rollback
      return { previous: oldData };
    },
    onError: (error, _, context) => {
      console.log(error);
      // Rollback to the previous state if mutation fails
      if (context?.previous) {
        queryClient.setQueryData(queryKey, context.previous);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey,
      });
    },
  });
};

export { useUpdatePhotoSequence };
